import { apiClientSecure } from '@/lib/api/index';
import { environment } from '@/config/environment';
import { localStorageVariables } from '@/types/localStorageVariables';
import { getPropertyId } from '@/hooks/useApiSequencing';

/**
 * TypeScript interface matching C# ResPortalStepLabelsModel
 * Maps C# types to TypeScript types:
 * - decimal -> number
 * - byte -> number
 * - DateTime -> string (ISO format)
 * - bool -> boolean
 */
export interface MenuItems {
    hMy: number;                           // decimal in C#
    Property?: number | null;              // decimal? in C#
    Company?: number | null;               // decimal? in C#
    StepID: number;                        // byte in C#
    StandardStep: boolean;                 // bool in C#
    SystemMenuName: string;               // string in C#
    SystemStepName: string;
    StepName: string;                  // string in C#
    Description: string;                  // string in C#
    PropertyType: number;                 // byte in C#
    CafeSystemFormId?: number | null;     // decimal? in C#
    CafeSystemFormSectionid?: number | null; // decimal? in C#
    Inactive: boolean;                    // bool in C#
    Weightage?: number | null;            // decimal? in C#
    UserCreatedBy: number;                // decimal in C#
    Created: string;                      // DateTime in C#, ISO string in TS
    UserModifiedBy: number;               // decimal in C#
    LastModified: string;                 // DateTime in C#, ISO string in TS
}

export interface ApiError {
    message: string;
    statusCode?: number;
    details?: unknown;
}

export class applayoutService {
    private readonly apiPrefix: string;
    private static readonly MENU_ITEMS_ENDPOINT = 'property/getmenuitems';

    constructor() {
        this.apiPrefix = environment.apiPrefix.rootPath;
    }

    /**
     * Fetches menu items from the server
     * @returns Promise containing array of menu items matching ResPortalStepLabelsModel structure
     * @throws {ApiError} When the API request fails
     */

    private menuItemsInFlight: Promise<MenuItems[]> | null = null;
    private menuItemsCache: MenuItems[] | null = null;

    /**
     * Clears the menu items cache to force a fresh fetch from the backend
     */
    clearCache(): void {
        this.menuItemsCache = null;
        this.menuItemsInFlight = null;
        console.log('🍔 applayoutService: Menu cache cleared');
    }

    async getMenuItems(): Promise<MenuItems[]> {

        // 1️⃣ Return cached result if already loaded
        if (this.menuItemsCache) {
            return this.menuItemsCache;
        }

        // 2️⃣ If request is already in progress, wait for it
        if (this.menuItemsInFlight) {
            return this.menuItemsInFlight;
        }

        // 3️⃣ Create a single in-flight request
        this.menuItemsInFlight = (async () => {
            try {
                const response = await apiClientSecure.get<MenuItems[]>(
                    applayoutService.MENU_ITEMS_ENDPOINT + '?propertyId=' + getPropertyId()
                );

                if (!response.data || !Array.isArray(response.data)) {
                    throw new Error('Invalid response format: expected array of menu items');
                }

                // Optional validation (keep or remove)
                response.data.forEach((item, index) => {
                    if (!item.SystemMenuName || !item.SystemStepName) {
                        console.warn(`Menu item ${index} missing name fields`, item);
                    }
                });

                // 4️⃣ Cache successful result
                this.menuItemsCache = response.data;

                return response.data;
            } catch (error) {
                const apiError: ApiError = {
                    message: error instanceof Error
                        ? error.message
                        : 'Failed to fetch menu items',
                    statusCode: (error as any)?.response?.status,
                    details: (error as any)?.response?.data
                };

                throw apiError;
            } finally {
                // 5️⃣ Clear in-flight promise after completion
                this.menuItemsInFlight = null;
            }
        })();

        return this.menuItemsInFlight;
    }


}
